import {MithrilComponent} from '@bitstillery/mithril'
import {
    activated_filters,
    clear_filter,
    lookup_filters_sort,
    restore_filters_sort,
    reset_filters_sort,
    url_to_filters_sort,
} from '@bitstillery/common/lib/filters'
import {classes, debounce, merge_deep, present, unique_id} from '@bitstillery/common/lib/utils'
import {watch} from '@bitstillery/mithril'
import {CollectionBulkColumn, FieldCheckbox, Icon, Spinner} from '@bitstillery/common/components'
import {grid_columns} from '@bitstillery/common/lib/collection'
import {$t, logger, view} from '@bitstillery/common/app'
import {nextTick} from '@bitstillery/mithril'
import m from '@bitstillery/mithril'
import {CollectionProxy, scroll_render} from '@bitstillery/common/lib/collection'
import type {CollectionColumnSetup, CollectionRow, CollectionState} from '@bitstillery/common/lib/collection'
import {unchecked} from '@bitstillery/common/lib/unchecked'

import type {FilterSelectSingleState} from '@bitstillery/common/types'

interface CollectionItemsAttrs<Row extends CollectionRow = CollectionRow> {
    className?: string
    /**
     * Set the context when you define the columns elsewhere, and want to access the CollectionItems context elsewhere.
     * The items only read which row is being edited; columns receive the whole value as their render context.
     */
    context?: unknown
    collection: CollectionProxy<Row>
    /** A description of how to render each row's columns and the collection header */
    columns?: CollectionColumnSetup<Row>

    disable_scroll?: boolean
    /** Number of rows to render outside visible area when virtualizing */
    overscan_rows?: number
    /** Type/variant for styling */
    type?: string
    /** Window the list by only rendering visible rows (optional) */
    virtualize?: boolean
    /** Whether the row has a checkbox for selection */
    has_selection?: boolean
    /**
     * A custom row renderer can be defined here. Virtualized, it renders every index of the window, so a
     * row that isn't loaded yet comes in as null (a placeholder); unvirtualized, it only gets rows. A
     * method, so a renderer for the unvirtualized case can declare its row as never null.
     */
    item?(
        row: Row | null,
        meta: {global_index: number; on_item_height_change?: (global_index: number, height_px: number) => void},
    ): m.Children
    /** Add custom behavior when clicking a row; passing it at all, even undefined, styles rows as clickable. */
    on_row_click?: ((row: Row) => unknown) | undefined
    on_selection_mode_change?: (mode: CollectionState['selection']['mode']) => unknown
    /** The row action buttons shown on hovering a row */
    row_actions?: (item: Row, context?: unknown, workflow_info?: {is_active: boolean; action_labels: string[]}) => m.Children
    /** Extra context space beneath the row, opened after clicking a row */
    row_detail?: (item: Row) => m.Children
    /** Row status icons at the end of a row */
    /** The row's status; `render` fills the status cell, which row_status_no_cell_status leaves out. */
    row_status?: (item: Row) => {render?: m.Children; type?: string}
    /** Whether to hide the row status column */
    row_status_no_cell_status?: boolean
    /** Row separator between different groups of items */
    row_separator?: (item: Row, index: number, items: Row[]) => {icon?: string; type: string; text: string} | null | undefined
    /** Extra class(es) on the row element, e.g. to nest a row under the one above it */
    row_class?: (item: Row, index: number, items: Row[]) => string | undefined
    /** Specify a different scroll container */
    scroll_container?: string
}

/**
 * Sub-pixel tolerance before a re-measure counts as a height change. Measured heights are kept
 * FRACTIONAL: a row's real height is content-driven (a 4/3 photo on a fractional column width, em-based
 * min-heights), so rounding it to whole pixels leaves the spacer math up to half a pixel short of the row
 * it replaces — and that error surfaces as a shift of the whole list every time the window's first row
 * changes. The tolerance only exists so ResizeObserver noise doesn't invalidate the geometry table.
 */
const HEIGHT_EPSILON_PX = 0.05

/** Cooldown (ms) after detail.artkey change before load-more observer is allowed; avoids spurious load when row_detail reflow moves sentinel into view */
const LOAD_MORE_DETAIL_COOLDOWN_MS = 350

/** Interval between re-attempts while a `poll_when_not_ready` collection sits in the `pending` state (its data is still being generated). */
const NOT_READY_POLL_MS = 3000

/** The meta's workflow filter, when the collection has one: a SELECT_SINGLE (`SelectWorkflow`). */
function meta_workflow_filter(collection: CollectionProxy): FilterSelectSingleState | undefined {
    const filter = collection.filters.workflow
    return filter?.type === 'SELECT_SINGLE' ? filter : undefined
}

/** The collection's rows: `State<Row>` maps a generic row's fields, which tsc can't relate back to `Row`. */
function state_rows<Row extends CollectionRow>(collection: CollectionProxy<Row>): Row[] {
    return unchecked<Row[]>(collection.state.items)
}

/** The row a ContextProvider is editing; any other render context (a component passing itself) has none. */
function editing_artkey(context: unknown): unknown {
    if (typeof context !== 'object' || context === null || !('data' in context)) {
        return undefined
    }
    const {data} = context
    return typeof data === 'object' && data !== null && 'entity_artkey' in data ? data.entity_artkey : undefined
}

export class CollectionItems<Row extends CollectionRow = CollectionRow> extends MithrilComponent<CollectionItemsAttrs<Row>> {
    scroll_container?: HTMLElement | null
    items_container?: HTMLElement | null
    debounced_cancel = false
    has_row_status = false
    id = `collection-items-${unique_id()}`

    /** Timer for re-polling a `pending` collection (see NOT_READY_POLL_MS); null when no poll is scheduled. */
    private not_ready_timer: ReturnType<typeof setTimeout> | null = null

    scroll_observer?: IntersectionObserver
    watchers: (() => void)[] = []
    collection?: CollectionProxy<Row>

    private last_viewport_range: {start: number; end: number} | null = null // Track last viewport range to prevent repeated calls

    private prefetch_inflight = new Set<string>()

    /** Measured DOM heights for virtualized items (global 0-based index → px). */
    private item_pixel_heights = new Map<number, number>()

    /**
     * Bumped whenever {@link item_pixel_heights} changes. Part of the {@link compute_virtual_geometry}
     * cache signature so measured-height edits invalidate the cached row table, but a pure scroll (which
     * changes nothing about geometry) reuses it instead of rebuilding an O(total_rows) table every frame.
     */
    private item_heights_version = 0

    /** Cached row-geometry keyed by a signature of every input; see {@link compute_virtual_geometry}. */
    private geometry_cache: {
        sig: string
        geom: {columns: number; gap: number; heights: number[]; row_tops: number[]; total_rows: number}
    } | null = null

    /** Last container width / view mode a column reconcile ran against; see {@link reconcile_columns_if_layout_shifted}. */
    private last_layout_width = -1
    private last_layout_mode = ''

    /** Last items-container width measure_grid ran against; a change triggers the scroll-anchor restore there. */
    private last_measured_width = 0

    /** View mode the card-height sampling in measure_grid last ran under; a change forces a resample. */
    private last_sampled_mode = ''

    /**
     * Whether the height used for rows nobody has rendered yet came from a real rendered card rather than
     * the CSS `--grid/list-item-height`. That var is only a starting guess and in the shop grid it is ~30px
     * taller than a card actually is (the card sizes to its 4/3 photo). Until it's corrected, every row
     * scrolled into for the FIRST time replaces the guess with its real height and the spacer above the
     * viewport shrinks by the difference — which is visible only when scrolling up, because that's the
     * direction where the rows being corrected are the ones above the reader.
     */
    private default_from_real_card = false

    /** rAF handle for the coalesced {@link measure_grid} pass; see {@link schedule_measure_grid}. */
    private measure_raf: number | null = null

    /** Heights reported by card ResizeObservers this frame, flushed in one version bump; see {@link on_virtual_item_height}. */
    private pending_item_heights: Map<number, number> | null = null
    private pending_heights_raf: number | null = null

    /** Extra padding below the last virtual row (px), from `--collection-scroll-bottom-gutter`; see measure_grid. */
    private bottom_gutter_px = 0

    /** Timestamp of last detail.artkey change; used to avoid spurious load-more when row_detail reflow moves the sentinel into view */
    private last_detail_change_at = 0

    /** Timeout ID for delayed scroll follow-up; cleared on teardown */
    private scroll_followup_timeout_id: ReturnType<typeof setTimeout> | null = null

    /** ResizeObserver for scroll correction when row_detail collapses/expands; disconnected on teardown */
    private scroll_resize_observer: ResizeObserver | null = null

    override async oninit(vnode: m.Vnode<CollectionItemsAttrs<Row>>) {
        view.collection = vnode.attrs.collection

        // The overscan is stored on the shared view state (that's what all the virtual math reads); the prop
        // is the per-mount override. Without this the `overscan_rows` attribute is silently ignored.
        if (typeof vnode.attrs.overscan_rows === 'number' && vnode.attrs.overscan_rows >= 0) {
            vnode.attrs.collection.state.view.overscan_rows = vnode.attrs.overscan_rows
        }

        if (vnode.attrs.collection.state.ready) {
            await this.oncollectionready(vnode, vnode.attrs.collection)
        }

        if (!vnode.attrs.collection.state.ready) {
            const on_ready = async () => {
                await this.oncollectionready(vnode, vnode.attrs.collection)
                unwatch()
            }
            let unwatch = watch(vnode.attrs.collection.state.$ready, () => {
                void on_ready()
            })
        }

        // Row status is a small column at the right that contains a few indicators.
        if (vnode.attrs.row_status && vnode.attrs.columns) {
            this.has_row_status = true
            // The columns are initialized at the top level and reused;
            // a cell-status column only needs to be added once.
            if (!vnode.attrs.row_status_no_cell_status) {
                if (!vnode.attrs.columns.find((i) => i.className === 'cell-status')) {
                    vnode.attrs.columns.push({
                        className: 'cell-status',
                        name: '',
                        // Never called: the last column renders the row_status instead.
                        render: () => null,
                        width: 'var(--coll-status-width)',
                    })
                }
            }
        }
    }

    override oncreate(vnode: m.Vnode<CollectionItemsAttrs<Row>>) {
        if (globalThis.__SSR_MODE__) {
            return
        }

        this.items_container = document.getElementById(this.id)
        const collection = vnode.attrs.collection

        if (vnode.attrs.virtualize) {
            const scroll_container = present(
                document.querySelector(`#${this.id}`)?.closest<HTMLElement>('.view'),
                'scroll container',
            )
            this.scroll_container = scroll_container
            // Coalesce to one redraw per frame: several scroll events can fire between paints, and without
            // the pending guard each would queue its own rAF redraw.
            let scroll_raf = 0
            const on_scroll_virtualized = () => {
                if (scroll_raf) {
                    return
                }
                scroll_raf = requestAnimationFrame(() => {
                    scroll_raf = 0
                    // Before the redraw, so it draws the new window with the rows a cache hit just placed.
                    this.sync_window(collection, this.calculate_viewport(collection, collection.state.total ?? 0, true))
                    // Flag this as a window-only redraw so rows can skip re-rendering (their data is unchanged;
                    // only the scroll offset moved). Needs a synchronous render so the flag brackets the actual
                    // work.
                    scroll_render.active = true
                    try {
                        m.redraw.sync()
                    } finally {
                        scroll_render.active = false
                    }
                })
            }
            scroll_container.addEventListener('scroll', on_scroll_virtualized, {passive: true})

            const grid_observer = new ResizeObserver(() => this.schedule_measure_grid(collection))
            grid_observer.observe(scroll_container)
            this.watchers.push(() => grid_observer.disconnect())
            this.watchers.push(() => this.cancel_scheduled_measure())
            this.watchers.push(() =>
                present(this.scroll_container, 'scroll container').removeEventListener('scroll', on_scroll_virtualized),
            )

            // Container-query card CSS tracks the collection width; virtual row math tracks ResizeObserver on
            // `.view`. Window / visualViewport resize still needs an explicit pass (iOS chrome, devtools drag,
            // delayed grid reflow) so measured heights and viewport range stay aligned.
            const measure_debounced = debounce(100, () => this.schedule_measure_grid(collection))
            const on_viewport_resize = () => {
                void measure_debounced()
            }
            window.addEventListener('resize', on_viewport_resize, {passive: true})
            this.watchers.push(() => window.removeEventListener('resize', on_viewport_resize))
            const vv = typeof window !== 'undefined' ? window.visualViewport : null
            if (vv) {
                vv.addEventListener('resize', on_viewport_resize, {passive: true})
                this.watchers.push(() => vv.removeEventListener('resize', on_viewport_resize))
            }

            this.schedule_measure_grid(collection)
            return
        }

        if (vnode.attrs.disable_scroll) {
            return
        }

        // Scroll-handling in regular non-virtualized mode uses an IntersectionObserver to
        // detect when the end of the container is in view. Load more items when the end is in view.
        if (vnode.attrs.scroll_container) {
            this.scroll_container = document.querySelector<HTMLElement>(vnode.attrs.scroll_container)
            if (!this.scroll_container) {
                throw new Error(
                    `CollectionItems component misses a DOM scrolling container (selector: ${vnode.attrs.scroll_container})`,
                )
            }
        } else {
            this.scroll_container = document.querySelector(`#${this.id}`)?.closest<HTMLElement>('.view') ?? null
        }

        const on_intersection = async (entries: IntersectionObserverEntry[]) => {
            const scroll_top = present(this.scroll_container, 'scroll container').scrollTop
            if (
                collection.state.loading ||
                !collection.state.ready ||
                present(entries[0], 'intersection entry').intersectionRatio <= 0
            ) {
                return
            }
            if (Date.now() - this.last_detail_change_at < LOAD_MORE_DETAIL_COOLDOWN_MS) {
                return
            }
            // Only advance when a page follows: a bumped offset without a query turns the next
            // filter response into an append onto the previous result set.
            if (collection.state.has_items) {
                collection.state.query.offset += collection.state.page_size
                await collection.query()
                // Restore scroll position after items are added; double rAF so restore runs after
                // Mithril redraw and next layout, avoiding jump when new items are rendered
                requestAnimationFrame(() => {
                    requestAnimationFrame(() => {
                        present(this.scroll_container, 'scroll container').scrollTop = scroll_top
                    })
                })
            }
        }
        this.scroll_observer = new IntersectionObserver(
            (entries) => {
                void on_intersection(entries)
            },
            {root: this.scroll_container, rootMargin: '8px'},
        )
        this.watchers.push(() => this.scroll_observer?.disconnect())

        const observable_element =
            document.querySelector(`#${this.id} + .loading-observable`) ||
            document.querySelector(`#${this.id} .loading-observable`)

        if (observable_element) {
            this.scroll_observer.observe(observable_element)

            // The first page can be shorter than the (CSS-grid) container, leaving no scroll space.
            // An IntersectionObserver only re-evaluates on scroll/observe, not on a pure root-size
            // change, so a late container resize (e.g. `--app-height` resolving after first paint)
            // would otherwise never trigger load-more. Re-observe on resize to force a re-evaluation;
            // existing guards (loading/ready/has_items) keep it from over-fetching once scrollable.
            const fill_observer = new ResizeObserver(() => {
                this.scroll_observer?.unobserve(observable_element)
                this.scroll_observer?.observe(observable_element)
            })
            fill_observer.observe(present(this.scroll_container, 'scroll container'))
            this.watchers.push(() => fill_observer.disconnect())
        }
    }

    override onremove(vnode: m.Vnode<CollectionItemsAttrs<Row>>) {
        vnode.attrs.collection.state.loading = true
        vnode.attrs.collection.state.items.splice(0, vnode.attrs.collection.state.items.length)
        // Clear cache on component removal
        if (vnode.attrs.collection.state.view.cache) {
            vnode.attrs.collection.clear_cache()
        }
        this.last_viewport_range = null
        this.clear_item_pixel_heights()

        if (this.not_ready_timer) {
            clearTimeout(this.not_ready_timer)
            this.not_ready_timer = null
        }

        this.cancel_scroll_followup()
        // Unset the view collection reference (mainly for counters)
        view.collection = null
        vnode.attrs.collection.state.query.offset = 0
        vnode.attrs.collection.state.items.splice(0, vnode.attrs.collection.state.items.length)

        this.debounced_cancel = true
        this.watchers.forEach((unwatch) => unwatch())

        if (!vnode.attrs.disable_scroll) {
            if (this.scroll_observer) {
                this.scroll_observer.disconnect()
            }
        }

        this.cancel_scheduled_measure()
        this.item_pixel_heights.clear()

        if (!vnode.attrs.collection.state.lookup.enabled) {
            void reset_filters_sort(vnode.attrs.collection)
        }

        vnode.attrs.collection.state.ready = false
    }

    async oncollectionready(vnode: m.Vnode<CollectionItemsAttrs<Row>>, collection: CollectionProxy<Row>) {
        if (!collection.state.endpoint.path) {
            return
        }

        // Reset viewport tracking when collection is ready
        this.last_viewport_range = null
        this.clear_item_pixel_heights()

        collection.state.loading = true
        collection.state.query.offset = 0
        collection.state.selection.ids.splice(0, collection.state.selection.ids.length)

        const restore_filters = await restore_filters_sort(collection)

        this.watchers.push(watch(collection.state.selection.$all, this.watch_select_all.bind(this, vnode)))
        this.watchers.push(watch(collection.state.selection.$mode, this.watch_select_mode.bind(this, vnode)))
        const on_sort_change = () => {
            void this.watch_sort(vnode)
        }
        this.watchers.push(watch(collection.state.sort.$by, on_sort_change))
        this.watchers.push(watch(collection.state.sort.$order, on_sort_change))
        this.watchers.push(
            watch(collection.state.detail.$artkey, () => {
                this.last_detail_change_at = Date.now()
            }),
        )
        const virtualize = vnode.attrs.virtualize
        this.watchers.push(
            watch(collection.state.$scroll_to_selection, () => {
                if (
                    collection.state.scroll_to_selection &&
                    !globalThis.__SSR_MODE__ &&
                    typeof requestAnimationFrame === 'function'
                ) {
                    // Double rAF so we run after Mithril redraw and layout (selection + detail change)
                    requestAnimationFrame(() => {
                        requestAnimationFrame(() => this.scroll_selected_row_to_top(collection, virtualize))
                    })
                }
            }),
        )

        if (vnode.attrs.virtualize) {
            this.watchers.push(
                watch(collection.state.view.$mode, () => {
                    // Restart at the top. The toggle lives in the toolbar row that scrolls away with
                    // the content, so a switch only happens near the top of the list — re-anchoring
                    // the pre-toggle viewport index bought nothing for its complexity.

                    // Reset the load guard so the new window is fetched even if its 1-based indices happen to
                    // match the pre-toggle range (mirrors watch_filters).
                    this.last_viewport_range = null
                    this.clear_item_pixel_heights()

                    if (globalThis.__SSR_MODE__ || typeof requestAnimationFrame !== 'function') {
                        this.measure_grid(collection)
                        this.jump_to_index(collection, 1)
                        return
                    }

                    // measure_grid reads the mode-specific track width (`--grid/list-product-min-width`), which
                    // only exists once the redraw has applied the new `.grid`/`.list` class and the grid has
                    // relaid out. `nextTick` is a microtask that resolves *before* the rAF-scheduled redraw, so
                    // it measured the stale layout and columns collapsed. Double rAF lands after that paint.
                    requestAnimationFrame(() =>
                        requestAnimationFrame(() => {
                            this.measure_grid(collection)
                            this.jump_to_index(collection, 1)
                            m.redraw()
                        }),
                    )
                }),
            )
        }

        logger.debug(`[collection-items] setup watchers (${collection.state.endpoint.path})`)
        if (collection.state.url_sync) {
            await nextTick()
            if (vnode.attrs.collection.state.url_sync && !restore_filters) {
                await url_to_filters_sort(collection)
            }
        }

        const on_filter_change = () => {
            void this.watch_filters(vnode)
        }
        // Watch filter data.
        for (const filter of Object.values(collection.filters)) {
            if (!filter.$selection) continue
            if (['boolean', 'number', 'string'].includes(typeof filter.selection)) {
                this.watchers.push(watch(filter.$selection, on_filter_change))
            } else {
                this.watchers.push(watch(filter.$selection, on_filter_change))
            }
        }

        if (!globalThis.__SSR_MODE__) {
            const on_history_change = async () => {
                const search_path = document.location.search
                if (!search_path) {
                    return
                }
                // Off for the filter change this is about to make: the URL is already what the visitor
                // asked for, and writing it back would push another entry on top of the one they just
                // travelled to. `lookup_filters_sort` turns it back on once that write is skipped —
                // which is why nothing may switch it off unless the filter assignment below follows.
                collection.state.url_sync = false
                await url_to_filters_sort(collection, search_path.substring(1))
            }
            /** Keep track of url changes while popstate doesn't navigate away from the collection page */
            const on_popstate = () => {
                void on_history_change()
            }
            window.addEventListener('popstate', on_popstate)
            this.watchers.push(() => window.removeEventListener('popstate', on_popstate))
        }

        if (!vnode.attrs.virtualize) {
            void collection.query()
            return
        }

        const view = collection.state.view
        view.page_base_offset = 0
        if (view.cache) {
            collection.clear_cache()
        }

        // We need the total amount from the API endpoint and a decent amount of rows to start with.
        const {items} = await this.perform_network_load(collection, 0, this.effective_virtual_columns(collection) * 6)

        if (items.length > 0) {
            collection.state.$items.value = items
            view.page_base_offset = 0
            collection.set_cached_items(0, items)
        }
    }

    /**
     * How many items share one “virtual row” (same CSS row). Both list and grid use CSS grid
     * (`auto-fit` / `repeat(…)`) so row width can be 1–N. `view.columns` is the count of
     * `.c-collection-product` elements that share the first row’s `offsetTop` (see
     * {@link measure_grid}); list must use the same value, not 1, or row_tops and padding
     * desync from the real layout.
     */
    private effective_virtual_columns(collection: CollectionProxy<Row>): number {
        return Math.max(1, collection.state.view.columns)
    }

    private clear_item_pixel_heights() {
        // Drop reports buffered this frame too — flushing them after the clear would resurrect heights
        // measured against the layout we just invalidated. The sampled default is measured against that
        // same layout, so it has to be re-earned as well.
        this.pending_item_heights = null
        this.default_from_real_card = false
        if (this.item_pixel_heights.size > 0) {
            this.item_pixel_heights.clear()
            this.item_heights_version++
        }
    }

    /**
     * Deterministic column count from the container width and the CSS track min-width, mirroring what
     * `grid-template-columns: repeat(auto-fit, minmax(<min>, 1fr))` resolves to.
     *
     * Counting same-`offsetTop` cards (the old approach) is circular: `auto-fit` collapses empty tracks, so
     * a small virtual window reports fewer columns, which shrinks the next window, which collapses the grid
     * further — bottoming out at one card. Reading the width instead is independent of how many cards are
     * currently rendered, so it can't feed that loop. Returns null when the track var isn't available
     * (caller then falls back to the DOM count).
     */
    private measure_columns_from_tracks(collection: CollectionProxy<Row>, style?: CSSStyleDeclaration): number | null {
        if (!this.items_container) {
            return null
        }
        const cs = style ?? getComputedStyle(this.items_container)
        const is_list = collection.state.view.mode === 'list'
        const min_w = this.parse_css_length_px(
            cs.getPropertyValue(is_list ? '--list-product-min-width' : '--grid-product-min-width'),
        )
        if (min_w == null) {
            return null
        }
        const pad_l = parseFloat(cs.paddingLeft) || 0
        const pad_r = parseFloat(cs.paddingRight) || 0
        const content_w = this.items_container.clientWidth - pad_l - pad_r
        if (content_w <= 0) {
            return null
        }
        const gap = parseFloat(cs.columnGap || cs.gap || '0') || 0
        return Math.max(1, Math.floor((content_w + gap) / (min_w + gap)))
    }

    /**
     * The single source of truth for the virtual column count. Prefers the width-derived value (stable
     * regardless of how many cards are rendered); falls back to counting the first DOM row for apps that
     * don't define the track vars (e.g. discover tables), but only when a second row confirms the width —
     * a collapsed single-row window can't distinguish "N columns" from "fewer cards than a row", and
     * counting it would drive the shrink-to-one-card feedback loop. Returns null when it can't tell.
     */
    private resolve_columns(
        collection: CollectionProxy<Row>,
        style?: CSSStyleDeclaration,
        get_cards?: () => HTMLElement[],
    ): number | null {
        const from_tracks = this.measure_columns_from_tracks(collection, style)
        if (from_tracks != null) {
            return from_tracks
        }
        const els = get_cards
            ? get_cards()
            : Array.from(this.items_container?.querySelectorAll<HTMLElement>('.c-collection-product') ?? [])
        if (els.length <= 1) {
            return null
        }
        const top0 = present(els[0], 'first card').offsetTop
        const same_row = els.filter((el) => el.offsetTop === top0).length
        return els.length > same_row ? same_row : null
    }

    /**
     * Keep `view.columns` correct without depending on redraw timing. Runs from `render_virtualized` (which
     * always executes *after* the redraw that applied the `.grid`/`.list` class), but only pays for the
     * getComputedStyle read when the container width or mode actually changed — a steady scroll early-returns
     * after one cheap clientWidth read. This is the timing-independent backstop to the mode-toggle measure.
     */
    private reconcile_columns_if_layout_shifted(collection: CollectionProxy<Row>) {
        if (globalThis.__SSR_MODE__ || !this.items_container) {
            return
        }
        const view = collection.state.view
        const width = this.items_container.clientWidth
        if (width === this.last_layout_width && view.mode === this.last_layout_mode) {
            return
        }
        this.last_layout_width = width
        this.last_layout_mode = view.mode
        const cols = this.resolve_columns(collection)
        if (cols != null && cols !== view.columns) {
            view.columns = cols
            this.clear_item_pixel_heights()
        }
    }

    /** Parse a CSS length like `112px`; used for custom properties on `.c-collection-items`. */
    private parse_css_length_px(raw: string): number | null {
        const t = raw.trim()
        if (!t) {
            return null
        }
        const n = Number(t.replace(/px$/i, ''))
        return Number.isFinite(n) && n > 0 ? n : null
    }

    /**
     * Keep `view.item_height_*` in sync with the subtree where `--list-item-height` / `--grid-item-height`
     * are actually set (e.g. mobile list uses 112px on `.c-collection-items` while :root still has 180px).
     * Reading only `document.documentElement` in collection init would inflate unmeasured row sizes and break
     * total scroll / padding math for virtual lists.
     *
     * A guess only, and only until the sampling below has a real card to measure: re-adopting the CSS value
     * afterwards would undo that measurement on every resize and swing the spacer by the difference.
     */
    private sync_virtual_defaults_from_css(collection: CollectionProxy<Row>, style?: CSSStyleDeclaration): boolean {
        if (!this.items_container || this.default_from_real_card) {
            return false
        }
        const view = collection.state.view
        const cs = style ?? getComputedStyle(this.items_container)
        let changed = false
        const list_h = this.parse_css_length_px(cs.getPropertyValue('--list-item-height'))
        if (list_h != null && view.item_height_list !== list_h) {
            view.item_height_list = list_h
            changed = true
        }
        const grid_h = this.parse_css_length_px(cs.getPropertyValue('--grid-item-height'))
        if (grid_h != null && view.item_height_grid !== grid_h) {
            view.item_height_grid = grid_h
            changed = true
        }
        return changed
    }

    /**
     * Height used for “how many rows intersect the viewport” (virtual range).
     * Subtracts overlap from fixed siblings such as the portal shop `.shop-bottom-bar`, which sits on
     * top of the list while `.view`’s `clientHeight` still spans the full grid cell.
     */
    private get_scrollport_height_for_virtual_range(): number {
        const sc = this.scroll_container
        if (!sc || globalThis.__SSR_MODE__) {
            return 0
        }
        let h = sc.clientHeight
        const grid = sc.closest('.shop-grid')
        const bar = grid?.querySelector<HTMLElement>(':scope > .shop-bottom-bar')
        if (bar) {
            const bstyle = getComputedStyle(bar)
            if (bstyle.display === 'none' || bstyle.visibility === 'hidden') {
                // Tablet/desktop: bar may stay in the DOM with `display: none` (e.g. portal shop).
            } else {
                const br = bar.getBoundingClientRect()
                if (br.height > 0.5) {
                    const sr = sc.getBoundingClientRect()
                    const overlap = Math.max(0, sr.bottom - br.top)
                    h = Math.max(1, h - overlap)
                }
            }
        }
        return Math.max(1, h)
    }

    /**
     * Coalesce every resize signal (the `.view` ResizeObserver, window/visualViewport resize) into one
     * {@link measure_grid} per frame, run from a rAF rather than inside the observer callback.
     *
     * Measuring inside the callback meant a live window drag paid the full pass once per observation —
     * and because measure_grid reads `offsetHeight`, each pass forced a synchronous layout *during* the
     * observer delivery, so the browser relaid out several times per frame and dropped frames while the
     * list was being scrolled.
     */
    private schedule_measure_grid(collection: CollectionProxy<Row>) {
        if (globalThis.__SSR_MODE__ || typeof requestAnimationFrame !== 'function') {
            this.measure_grid(collection)
            return
        }
        if (this.measure_raf != null) {
            return
        }
        this.measure_raf = requestAnimationFrame(() => {
            this.measure_raf = null
            this.measure_grid(collection)
        })
    }

    private cancel_scheduled_measure() {
        if (this.measure_raf != null) {
            cancelAnimationFrame(this.measure_raf)
            this.measure_raf = null
        }
        if (this.pending_heights_raf != null) {
            cancelAnimationFrame(this.pending_heights_raf)
            this.pending_heights_raf = null
        }
        this.pending_item_heights = null
    }

    /** Apply the heights buffered by {@link on_virtual_item_height} as a single geometry invalidation. */
    private flush_pending_item_heights() {
        const pending = this.pending_item_heights
        this.pending_item_heights = null
        if (!pending) {
            return
        }
        let changed = false
        for (const [index, h] of pending) {
            const known = this.item_pixel_heights.get(index)
            if (known === undefined || Math.abs(known - h) >= HEIGHT_EPSILON_PX) {
                this.item_pixel_heights.set(index, h)
                changed = true
            }
        }
        if (changed) {
            this.item_heights_version++
        }
    }

    /**
     * ResizeObserver from virtualized row cards — keeps spacer math in sync with variable row heights.
     *
     * A resize changes every visible card at once, so this fires N times per frame. Buffering the reports
     * and bumping {@link item_heights_version} once per frame collapses N invalidations of the
     * O(total_rows) geometry table into one rebuild, instead of one per card.
     */
    private on_virtual_item_height = (collection: CollectionProxy<Row>, global_index: number, height_px: number) => {
        const h = Math.max(1, height_px)
        // A real card just reported its height, so the CSS guess for every row nobody has rendered can be
        // replaced by a measurement. measure_grid owns that sampling (it averages the rendered cards); this
        // only asks it to run, and only until it has an answer, so a steady scroll pays nothing.
        if (!this.default_from_real_card) {
            this.schedule_measure_grid(collection)
        }
        const known = this.item_pixel_heights.get(global_index)
        if (known !== undefined && Math.abs(known - h) < HEIGHT_EPSILON_PX) {
            return
        }
        if (globalThis.__SSR_MODE__ || typeof requestAnimationFrame !== 'function') {
            this.item_pixel_heights.set(global_index, h)
            this.item_heights_version++
            return
        }
        ;(this.pending_item_heights ??= new Map()).set(global_index, h)
        if (this.pending_heights_raf == null) {
            this.pending_heights_raf = requestAnimationFrame(() => {
                this.pending_heights_raf = null
                this.flush_pending_item_heights()
                m.redraw()
            })
        }
    }

    private default_item_height_px(collection: CollectionProxy<Row>) {
        const view = collection.state.view
        return view.mode === 'list' ? view.item_height_list : view.item_height_grid
    }

    private item_content_height_px(collection: CollectionProxy<Row>, global_index: number) {
        return this.item_pixel_heights.get(global_index) ?? this.default_item_height_px(collection)
    }

    private compute_virtual_geometry(collection: CollectionProxy<Row>, total_count: number) {
        const view = collection.state.view
        const columns = this.effective_virtual_columns(collection)
        const gap = view.row_gap

        // The row table only depends on these inputs; a pure scroll changes none of them. Reuse the cached
        // table across frames so scrolling doesn't rebuild an O(total_rows) heights/row_tops pair each redraw.
        // `item_heights_version` folds in every per-item measured-height edit (see on_virtual_item_height).
        const default_h = this.default_item_height_px(collection)
        const sig = `${view.mode}|${columns}|${gap}|${default_h}|${total_count}|${this.item_heights_version}`
        if (this.geometry_cache?.sig === sig) {
            return this.geometry_cache.geom
        }

        const total_rows = Math.max(0, Math.ceil(total_count / columns))
        const heights: number[] = []
        for (let r = 0; r < total_rows; r++) {
            let max_h = 0
            for (let c = 0; c < columns; c++) {
                const idx = r * columns + c
                if (idx >= total_count) {
                    break
                }
                max_h = Math.max(max_h, this.item_content_height_px(collection, idx))
            }
            heights.push(max_h)
        }
        const row_tops: number[] = [0]
        let row_top = 0
        for (const [r, height] of heights.entries()) {
            row_top += height + (r < total_rows - 1 ? gap : 0)
            row_tops.push(row_top)
        }
        const geom = {columns, gap, heights, row_tops, total_rows}
        this.geometry_cache = {sig, geom}
        return geom
    }

    /** First row whose span [row_tops[r], row_tops[r+1]) still intersects scroll_top. */
    private first_visible_row(row_tops: number[], total_rows: number, scroll_top: number) {
        if (total_rows === 0) {
            return 0
        }
        let lo = 0
        let hi = total_rows - 1
        while (lo < hi) {
            const mid = (lo + hi) >> 1
            if ((row_tops[mid + 1] ?? Infinity) <= scroll_top) {
                lo = mid + 1
            } else {
                hi = mid
            }
        }
        return Math.min(lo, total_rows - 1)
    }

    /**
     * Calculate viewport range based on scroll position and layout.
     * Uses per-item measured heights when virtualizing; otherwise uniform CSS height.
     */
    private calculate_viewport(collection: CollectionProxy<Row>, total_count: number, virtualize: boolean) {
        const view = collection.state.view
        const is_list = view.mode === 'list'
        const item_height = is_list ? view.item_height_list : view.item_height_grid
        const columns = this.effective_virtual_columns(collection)
        const scroll_top = this.scroll_container?.scrollTop ?? 0
        const viewport_h = virtualize
            ? this.get_scrollport_height_for_virtual_range()
            : Math.max(1, this.scroll_container?.clientHeight ?? 0)
        const overscan = view.overscan_rows

        if (!virtualize || total_count <= 0) {
            const row_h = item_height + view.row_gap
            const first_row = row_h > 0 ? Math.floor(scroll_top / row_h) : 0
            const visible_rows = row_h > 0 ? Math.ceil(viewport_h / row_h) : 10
            let start_row = Math.max(0, first_row - overscan)
            let end_row = first_row + visible_rows + overscan
            const total_rows = Math.ceil(total_count / columns)
            const row_height_px = Math.max(1, Math.round(row_h))
            start_row = Math.max(0, Math.min(start_row, Math.max(0, total_rows - 1)))
            end_row = Math.min(end_row, Math.max(0, total_rows - 1))
            const start_index = start_row * columns
            const end_index = Math.min(total_count, (end_row + 1) * columns)
            return {
                start_index,
                end_index,
                ...this.load_window(view, start_row, end_row, total_rows, columns, total_count),
                start_row,
                end_row,
                row_height_px,
                total_rows,
                item_height,
                is_list,
                row_h,
                columns,
                overscan,
            }
        }

        const {columns: vcols, heights, row_tops, total_rows} = this.compute_virtual_geometry(collection, total_count)
        if (total_rows === 0) {
            return {
                start_index: 0,
                end_index: 0,
                load_start_index: 0,
                load_end_index: 0,
                start_row: 0,
                end_row: 0,
                row_height_px: Math.max(1, Math.round(item_height + view.row_gap)),
                total_rows: 0,
                item_height,
                is_list,
                row_h: item_height + view.row_gap,
                columns: vcols,
                overscan,
            }
        }

        let start_row = this.first_visible_row(row_tops, total_rows, scroll_top)
        let end_row = start_row
        const view_bottom = scroll_top + viewport_h
        while (end_row < total_rows - 1 && (row_tops[end_row + 1] ?? Infinity) < view_bottom) {
            end_row++
        }

        start_row = Math.max(0, start_row - overscan)
        end_row = Math.min(total_rows - 1, end_row + overscan)

        const start_index = start_row * vcols
        const end_index = Math.min(total_count, (end_row + 1) * vcols)
        const avg_h = heights.length > 0 ? heights.reduce((a, b) => a + b, 0) / heights.length : item_height
        const row_height_px = Math.max(1, Math.round(avg_h + view.row_gap))

        return {
            start_index,
            end_index,
            ...this.load_window(view, start_row, end_row, total_rows, vcols, total_count),
            start_row,
            end_row,
            row_height_px,
            total_rows,
            item_height,
            is_list,
            row_h: avg_h + view.row_gap,
            columns: vcols,
            overscan,
        }
    }

    /**
     * Index range to KEEP LOADED — the rendered window padded by `view.load_ahead_rows` on both sides.
     * Rendering an index whose item hasn't arrived yet draws a placeholder card, so fetching only what
     * is rendered means every flick outruns its own request and lands on blanks; the pad buys the
     * network the rows it needs to stay ahead.
     */
    private load_window(
        view: CollectionProxy['state']['view'],
        start_row: number,
        end_row: number,
        total_rows: number,
        columns: number,
        total_count: number,
    ) {
        const ahead = Math.max(0, view.load_ahead_rows)
        const load_start_row = Math.max(0, start_row - ahead)
        const load_end_row = Math.max(load_start_row, Math.min(Math.max(0, total_rows - 1), end_row + ahead))
        return {
            load_start_index: load_start_row * columns,
            load_end_index: Math.min(total_count, (load_end_row + 1) * columns),
        }
    }

    private jump_to_index(collection: CollectionProxy<Row>, target_index: number) {
        logger.info(`[items] jump_to_index ${target_index}`)
        // Convert 1-based index to 0-based
        const target_0_index = Math.max(0, target_index - 1)
        if (target_0_index >= (collection.state.total ?? 0)) return

        const view = collection.state.view
        const vcols = this.effective_virtual_columns(collection)
        const target_row = Math.floor(target_0_index / vcols)

        let scroll_top = 0
        if (this.scroll_container) {
            const geom = this.compute_virtual_geometry(collection, collection.state.total ?? 0)
            scroll_top = geom.row_tops[Math.min(target_row, Math.max(0, geom.total_rows - 1))] ?? 0
        }

        const loaded_start = view.page_base_offset
        const loaded_end = loaded_start + collection.state.items.length
        const target_within_loaded = target_0_index >= loaded_start && target_0_index < loaded_end
        if (!target_within_loaded) {
            collection.state.items.splice(0, collection.state.items.length)
            view.page_base_offset = target_0_index
        }

        if (this.scroll_container) {
            this.scroll_container.scrollTo({top: scroll_top, behavior: 'instant'})
        }
    }

    /**
     * Scroll the selected row to the top of the visible scroll area (just below CollectionHeader).
     * Called when scroll_to_selection is set (e.g. from PanelContext select_next/select_previous).
     * Gets the row's top position and scrolls to it; ResizeObserver + delayed retry correct for
     * layout shifts when row_detail collapses.
     */
    private scroll_selected_row_to_top(collection: CollectionProxy<Row>, _virtualize?: boolean) {
        collection.state.scroll_to_selection = false
        if (!this.scroll_container || !this.items_container) return
        const artkey = collection.state.selection.ids[0] as number | string | null | undefined
        if (artkey === undefined || artkey === null) return

        this.scroll_row_to_top(collection, artkey)
        this.schedule_scroll_followup(collection, artkey)
    }

    private scroll_row_to_top(_collection: CollectionProxy<Row>, artkey: number | string) {
        const row = this.items_container?.querySelector<HTMLElement>(`[data-artkey="${artkey}"]`)
        if (!row || !this.scroll_container) return

        const header = this.scroll_container.querySelector<HTMLElement>('.c-collection-header')
        const offset_below_top = header
            ? header.getBoundingClientRect().height + (parseFloat(getComputedStyle(header).marginBottom) || 0)
            : 0
        const row_top = row.getBoundingClientRect().top
        const container_top = this.scroll_container.getBoundingClientRect().top
        const new_scroll_top = Math.max(
            0,
            Math.min(
                this.scroll_container.scrollTop + (row_top - container_top) - offset_below_top,
                this.scroll_container.scrollHeight - this.scroll_container.clientHeight,
            ),
        )
        this.scroll_container.scrollTo({top: new_scroll_top, behavior: 'instant'})
    }

    private schedule_scroll_followup(collection: CollectionProxy<Row>, artkey: number | string) {
        this.cancel_scroll_followup()

        const reapply = () => {
            if (this.scroll_container && collection.state.selection.ids[0] === artkey) {
                this.scroll_row_to_top(collection, artkey)
            }
        }

        this.scroll_followup_timeout_id = setTimeout(() => {
            this.scroll_followup_timeout_id = null
            reapply()
            this.scroll_resize_observer?.disconnect()
            this.scroll_resize_observer = null
        }, 400)

        if (this.items_container) {
            this.scroll_resize_observer = new ResizeObserver(() => reapply())
            this.scroll_resize_observer.observe(this.items_container)
        }
    }

    private cancel_scroll_followup() {
        if (this.scroll_followup_timeout_id !== null) {
            clearTimeout(this.scroll_followup_timeout_id)
            this.scroll_followup_timeout_id = null
        }
        this.scroll_resize_observer?.disconnect()
        this.scroll_resize_observer = null
    }

    private async maybe_load_more(
        collection: CollectionProxy<Row>,
        target_offset: number,
        target_limit: number,
        range: {start: number; end: number},
    ) {
        const view = collection.state.view

        if (
            collection.state.loading ||
            !collection.state.ready ||
            (!collection.state.has_items && collection.state.items.length === 0)
        ) {
            return
        }

        if (range.start && range.end) {
            if (
                this.last_viewport_range &&
                this.last_viewport_range.start === range.start &&
                this.last_viewport_range.end === range.end
            ) {
                return
            }
            this.last_viewport_range = {end: range.end, start: range.start}
        }

        if (view.cache) {
            void this.schedule_expired_refresh(collection, range)
        }

        // If the requested window is partially covered by the currently loaded contiguous slice,
        // only load the missing tail to avoid re-requesting items we already have.
        const current_start = view.page_base_offset
        const current_end = current_start + collection.state.items.length

        if (target_offset >= current_start && target_offset < current_end) {
            const target_end = target_offset + target_limit
            if (target_end <= current_end) {
                // The requested window is fully covered already.
                return
            }

            const missing_offset = current_end
            const missing_limit = target_end - current_end
            await this.perform_network_load(collection, missing_offset, missing_limit)
            return
        }

        if (view.cache) {
            const cache_result = collection.get_cached_items(target_offset, target_limit)
            // All indices cached and valid: use cached items
            if (cache_result.items !== null) {
                // Check if items are already in state - if so, don't replace them to preserve references
                const state_start = target_offset - view.page_base_offset
                const state_end = state_start + cache_result.items.length
                // `window_already_loaded` indicates whether the requested cached window already maps
                // fully onto the current `state.items` slice (no gaps or overflows). When true,
                // we keep the existing state references so scroll position and item indices stay stable.
                const window_already_loaded =
                    state_start >= 0 && state_end <= collection.state.items.length && state_end > state_start

                if (window_already_loaded) {
                    return
                } else {
                    this.synchronize_cached_window(collection, target_offset, cache_result.items)
                    return
                }
            }
        }

        await this.perform_network_load(collection, target_offset, target_limit)
    }

    private measure_grid(collection: CollectionProxy<Row>) {
        if (globalThis.__SSR_MODE__ || !this.items_container) {
            return
        }

        // A container width change re-flows every row (the photo height rides the column width),
        // which reads as the list "scrolling by itself" while resizing. Capture which item tops
        // the viewport under the OLD geometry now, restore it under the new geometry below.
        const container_width = this.items_container.clientWidth
        let scroll_anchor: {index: number; offset: number} | null = null
        if (
            this.last_measured_width &&
            container_width !== this.last_measured_width &&
            this.scroll_container &&
            (collection.state.total ?? 0) > 0
        ) {
            const geom = this.compute_virtual_geometry(collection, collection.state.total ?? 0)
            if (geom.total_rows > 0) {
                const scroll_top = this.scroll_container.scrollTop
                const row = this.first_visible_row(geom.row_tops, geom.total_rows, scroll_top)
                scroll_anchor = {index: row * geom.columns, offset: scroll_top - (geom.row_tops[row] ?? 0)}
            }
        }
        const width_changed = container_width !== this.last_measured_width
        this.last_measured_width = container_width

        // One computed-style read for the whole pass: each `getComputedStyle`/`getPropertyValue` after a
        // style invalidation can force a recalc, and a live resize invalidates on every frame.
        const cs = getComputedStyle(this.items_container)

        let layout_changed = this.sync_virtual_defaults_from_css(collection, cs)
        const view = collection.state.view

        // Only the DOM-counting fallback in resolve_columns needs the card list, and the height sampling
        // below is skipped unless the width moved — so pay for the query lazily.
        let cards: HTMLElement[] | null = null
        const get_cards = () =>
            (cards ??= Array.from(
                present(this.items_container, 'items container').querySelectorAll<HTMLElement>('.c-collection-product'),
            ))

        const next_columns = this.resolve_columns(collection, cs, get_cards)
        if (next_columns != null && (view.columns === 0 || next_columns !== view.columns)) {
            const prev_columns = view.columns
            view.columns = next_columns
            logger.debug(`[virtual-scroll] columns changed: ${prev_columns} -> ${view.columns}`)
            layout_changed = true
            this.clear_item_pixel_heights()
        }
        const gap_raw = cs.rowGap || cs.gap || '0'
        const gap_num = parseFloat(gap_raw) || 0
        if (view.row_gap === 0 || gap_num !== view.row_gap) {
            view.row_gap = gap_num
            layout_changed = true
        }

        // Breathing room below the last row. The JS-owned bottom spacer collapses to 0 at the end of the
        // list, clobbering any CSS padding-bottom on the container, so the gutter is folded into the spacer
        // instead (see render_virtualized). Opt-in per consumer via `--collection-scroll-bottom-gutter`.
        this.bottom_gutter_px = this.parse_css_length_px(cs.getPropertyValue('--collection-scroll-bottom-gutter')) ?? 0

        // The rendered card height often differs from the CSS default: in the LIST from container
        // queries (`height: unset`, multi-column + narrow product width); in the GRID because the 4/3
        // photo's height scales with the column width, so a card that fills a `1fr` track is content-
        // sized, not a fixed stride. Sample the real cards so `row_tops` track reality instead of a
        // stale default (too large ⇒ oversized rows ⇒ half-empty view; too small ⇒ overlap).
        //
        // Only worth resampling when the geometry actually moved: reading the height off every rendered
        // card forces a synchronous layout, and a height-only container resize (panel toggles, the mobile
        // bottom bar, the URL bar) can't change a card's height. Per-card ResizeObservers keep the *measured*
        // heights current regardless; this loop only maintains the default used for unmeasured rows.
        //
        // `!default_from_real_card` is the exception that must run: the first pass happens while the window
        // is still placeholders, which are filtered out here, so it ends with nothing sampled and the CSS
        // guess left in place — and nothing would ever ask again, since the width never changes after mount.
        if (width_changed || layout_changed || this.last_sampled_mode !== view.mode || !this.default_from_real_card) {
            this.last_sampled_mode = view.mode
            const real_cards = get_cards().filter((el) => !el.classList.contains('placeholder'))
            if (real_cards.length >= 1) {
                let sum = 0
                let n = 0
                for (const el of real_cards) {
                    // Sub-pixel: a card's height rides its 4/3 photo on a fractional column width, and the
                    // whole-pixel `offsetHeight` would leave the spacer a fraction short of every row it
                    // stands in for.
                    const oh = el.getBoundingClientRect().height
                    if (oh > 0) {
                        sum += oh
                        n++
                    }
                }
                if (n > 0) {
                    const sampled = Math.max(1, sum / n)
                    const is_list = collection.state.view.mode === 'list'
                    const current = is_list ? view.item_height_list : view.item_height_grid
                    this.default_from_real_card = true
                    if (Math.abs(current - sampled) >= 2) {
                        if (is_list) {
                            view.item_height_list = sampled
                        } else {
                            view.item_height_grid = sampled
                        }
                        layout_changed = true
                    }
                }
            }
        }

        if (scroll_anchor && this.scroll_container) {
            const geom = this.compute_virtual_geometry(collection, collection.state.total ?? 0)
            if (geom.total_rows > 0) {
                const row = Math.min(Math.floor(scroll_anchor.index / geom.columns), geom.total_rows - 1)
                const next_top = (geom.row_tops[row] ?? 0) + scroll_anchor.offset
                // Writing an unchanged scrollTop still emits a scroll event on some engines, which costs a
                // whole extra windowed redraw per resize frame for nothing.
                if (Math.abs(this.scroll_container.scrollTop - next_top) > 0.5) {
                    this.scroll_container.scrollTop = next_top
                }
            }
        }

        if (layout_changed) {
            this.sync_window(collection, this.calculate_viewport(collection, collection.state.total ?? 0, true))
            // `m.redraw` is already rAF-scheduled; this used to hop an extra frame, which showed up as the
            // window lagging a frame behind the layout during a resize drag.
            m.redraw()
        }
    }

    /**
     * While a `poll_when_not_ready` collection is `pending` (its data is still being generated and the
     * endpoint answers 404), re-attempt the first window on an interval. `maybe_load_more`'s guards
     * (`has_items` flips false on an empty 404 response) deliberately stop the render loop from retrying, so
     * the poll calls `perform_network_load` directly. Self-terminating: once a load succeeds
     * `reconcile_content` flips `content_state` to `normal` and the guard below stops rescheduling.
     */
    private schedule_not_ready_poll(collection: CollectionProxy<Row>) {
        if (this.not_ready_timer) {
            return
        }
        // A manually-populated collection (no endpoint — e.g. the CMS product carousel, re-supplied by
        // `AsyncData`) shows the pending widget but owns no query to poll; polling would hit the API root.
        if (!collection.state.endpoint.path) {
            return
        }
        const poll = async () => {
            this.not_ready_timer = null
            if (collection.state.content_state !== 'pending') {
                return
            }
            await this.perform_network_load(collection, 0, collection.state.page_size)
            m.redraw()
        }
        this.not_ready_timer = setTimeout(() => {
            void poll()
        }, NOT_READY_POLL_MS)
    }

    private async perform_network_load(collection: CollectionProxy<Row>, target_offset: number, target_limit: number) {
        const {items, total} = await collection.query_virtualized(target_offset, target_limit)
        collection.state.total = total

        // Cache the loaded items if caching is enabled
        if (items.length > 0) {
            collection.set_cached_items(target_offset, items)
        }

        this.reconcile_loaded_window(collection, target_offset, items)
        collection.state.has_items = collection.state.items.length < (collection.state.total ?? 0)

        this.measure_grid(collection)
        if (collection.state.view.cache && collection.state.has_items) {
            await this.prefetch_next_cached_window(collection, target_offset, target_limit)
        }
        return {items, total}
    }

    private async prefetch_next_cached_window(collection: CollectionProxy<Row>, current_offset: number, current_limit: number) {
        const next_offset = current_offset + current_limit
        if (next_offset < 0) {
            return
        }

        if (collection.state.total && next_offset >= collection.state.total) {
            return
        }

        const cache_status = collection.get_cached_items(next_offset, current_limit)
        if (!cache_status.has_missing && cache_status.expired_indices.length === 0) {
            return
        }

        const key = `${next_offset}:${current_limit}`
        if (this.prefetch_inflight.has(key)) {
            return
        }
        this.prefetch_inflight.add(key)

        try {
            const {items, status_code} = await collection.fetch_items(next_offset, current_limit)
            if (status_code > 299 || status_code === 204 || status_code === 206) {
                return
            }
            if (items.length > 0) {
                collection.set_cached_items(next_offset, items)
            }
        } finally {
            this.prefetch_inflight.delete(key)
        }
    }

    private reconcile_loaded_window(collection: CollectionProxy<Row>, target_offset: number, loaded_items: Row[]) {
        const view = collection.state.view
        const is_loading_before = target_offset < view.page_base_offset
        const is_gap_forward = target_offset > view.page_base_offset + collection.state.items.length
        const is_reloading_expired =
            target_offset >= view.page_base_offset && target_offset < view.page_base_offset + collection.state.items.length
        const prev_length = collection.state.items.length

        if (is_loading_before || is_gap_forward) {
            collection.state.items.splice(0, collection.state.items.length, ...loaded_items)
            view.page_base_offset = target_offset
        } else if (is_reloading_expired) {
            const array_start = target_offset - view.page_base_offset
            const array_end = Math.min(array_start + loaded_items.length, collection.state.items.length)

            for (const [i, loaded_item] of loaded_items.entries()) {
                const state_idx = array_start + i
                if (state_idx < collection.state.items.length) {
                    merge_deep(collection.state.items[state_idx], loaded_item)
                } else {
                    collection.state.items.push(loaded_item)
                }
            }
            if (array_end - array_start > loaded_items.length) {
                collection.state.items.splice(array_start + loaded_items.length, array_end - array_start - loaded_items.length)
            }
        } else if (view.page_base_offset === 0 && prev_length === 0) {
            view.page_base_offset = target_offset
            collection.state.items.splice(0, collection.state.items.length, ...loaded_items)
        } else {
            collection.state.items.push(...loaded_items)
        }
    }

    private render(vnode: m.Vnode<CollectionItemsAttrs<Row>>, items: Row[]) {
        const selection = vnode.attrs.collection.state.selection
        let has_selection = vnode.attrs.has_selection || ['select', 'select_visible', 'deselect'].includes(selection.mode)
        // Non-virtualized path: render all rows directly
        return items.map((item, index: number) => {
            let row_status: ReturnType<NonNullable<CollectionItemsAttrs<Row>['row_status']>> | undefined
            let row_separator: ReturnType<NonNullable<CollectionItemsAttrs<Row>['row_separator']>>
            let row_detail: m.Children

            if (vnode.attrs.row_separator) {
                row_separator = vnode.attrs.row_separator(item, index, items)
            }

            if (vnode.attrs.row_detail) {
                row_detail = vnode.attrs.row_detail(item)
            }
            if (vnode.attrs.row_status) {
                row_status = vnode.attrs.row_status(item)
            }

            // Override row_status type for workflow completion
            if (row_status && vnode.attrs.collection.is_marked(item.artkey)) {
                row_status = {...row_status, type: 'default'}
            }

            if (vnode.attrs.item) {
                return vnode.attrs.item(item, {
                    global_index: index,
                })
            }

            if (!vnode.attrs.columns) {
                throw new Error('item or column renderer is required')
            }

            const grid_template_cols = grid_columns(vnode.attrs.columns, has_selection, vnode.attrs.collection.state.bulk_mode)

            const item_elements = [
                <div
                    key={`${vnode.attrs.collection._id}-${item.artkey}`}
                    data-artkey={String(item.artkey)}
                    className={classes(
                        'item',
                        this.has_row_status ? `item-type-${row_status?.type || 'default'}` : 'item-type-default',
                        vnode.attrs.row_class?.(item, index, items),
                        {
                            changed:
                                vnode.attrs.collection.state.bulk_mode &&
                                present(vnode.attrs.collection.bulk, 'collection bulk').state.rows[item.artkey]?.changed,
                            detail: vnode.attrs.collection.state.detail.artkey === item.artkey,
                            'has-row-detail': !!row_detail,
                            invalid:
                                vnode.attrs.collection.state.bulk_mode &&
                                present(vnode.attrs.collection.bulk, 'collection bulk').state.rows[item.artkey]?.invalid,
                            marked: vnode.attrs.collection.is_marked(item.artkey),
                            odd: index % 2 === 0,
                            even: index % 2 === 1,
                            selected:
                                vnode.attrs.collection.state.selection.ids.includes(item.artkey) &&
                                !vnode.attrs.collection.state.selection.all,
                        },
                    )}
                    onclick={(e) => {
                        // Row-details elements must block the row_detail toggle,
                        // or actions like copy-paste are impossible.
                        if (e.target instanceof Element) {
                            const closest = e.target.closest('.no-click')
                            if (closest) {
                                return
                            }
                        }

                        if ('row_detail' in vnode.attrs) {
                            const detail = vnode.attrs.collection.state.detail
                            if (detail.artkey !== item.artkey) {
                                detail.artkey = item.artkey
                                detail.key = `${vnode.attrs.collection._id}-${item.artkey}`
                            } else {
                                detail.key = ''
                                detail.artkey = null
                            }
                        }
                        if (vnode.attrs.on_row_click) {
                            vnode.attrs.on_row_click(item)
                        }
                    }}
                >
                    <div className='cells' style={`grid-template-columns: ${grid_template_cols}`}>
                        <div className='cell-selection no-click'>
                            {(() => {
                                const bulk = vnode.attrs.collection.bulk
                                const is_bulk_mode = vnode.attrs.collection.state.bulk_mode
                                const is_context_selected =
                                    vnode.attrs.context &&
                                    editing_artkey(vnode.attrs.context) === item.artkey &&
                                    bulk &&
                                    Object.keys(bulk.columns).some((colId) => {
                                        // Check if any column has this id and bulk column definition
                                        return vnode.attrs.columns?.some((col) => col.id === colId)
                                    })

                                if (is_bulk_mode || is_context_selected) {
                                    const active_bulk = present(bulk, 'collection bulk')
                                    if (!(item.artkey in active_bulk.state.rows)) {
                                        active_bulk.state.rows[item.artkey] = {
                                            changed: false,
                                            invalid: false,
                                            item,
                                            loading: false,
                                            persist: false,
                                            /** Row-level field mapping to the original model value */
                                            restore: {},
                                        }
                                    }

                                    const bulk_row = present(active_bulk.state.rows[item.artkey], 'bulk row')
                                    if (!active_bulk._fields[item.artkey]) {
                                        active_bulk._fields[item.artkey] = active_bulk.fields(bulk_row)
                                    }

                                    return (
                                        <div className='actions'>
                                            {active_bulk.row_actions?.(bulk_row).map((action) => action)}
                                        </div>
                                    )
                                }
                                return undefined
                            })()}
                            {has_selection && (
                                <FieldCheckbox
                                    disabled={
                                        vnode.attrs.collection.state.loading ||
                                        vnode.attrs.collection.state.selection.locked.includes(item.artkey)
                                    }
                                    onAfterChange={(newValue) => {
                                        if (!newValue) {
                                            vnode.attrs.collection.item_select(item)
                                        } else {
                                            vnode.attrs.collection.item_deselect(item)
                                        }
                                    }}
                                    computed={() => {
                                        if (vnode.attrs.collection.state.selection.mode === 'select_visible') {
                                            return vnode.attrs.collection.state.selection.ids.includes(item.artkey)
                                        }
                                        if (vnode.attrs.collection.state.selection.all) {
                                            return !vnode.attrs.collection.state.selection.ids.includes(item.artkey)
                                        }
                                        return vnode.attrs.collection.state.selection.ids.includes(item.artkey)
                                    }}
                                />
                            )}
                        </div>

                        {vnode.attrs.columns.map((column, index, columns) => {
                            const is_last_column = index === columns.length - 1
                            let rendered_row
                            const bulk = vnode.attrs.collection.bulk
                            const is_bulk_mode = vnode.attrs.collection.state.bulk_mode
                            const is_context_selected =
                                vnode.attrs.context &&
                                editing_artkey(vnode.attrs.context) === item.artkey &&
                                bulk &&
                                column.id &&
                                column.id in bulk.columns

                            // Render bulk column if in bulk mode or context-selected with bulk column definition
                            if ((is_bulk_mode || is_context_selected) && bulk && column.id && column.id in bulk.columns) {
                                // Ensure bulk row is initialized
                                if (!(item.artkey in bulk.state.rows)) {
                                    bulk.state.rows[item.artkey] = {
                                        changed: false,
                                        invalid: false,
                                        item,
                                        loading: false,
                                        persist: false,
                                        restore: {},
                                    }
                                }
                                const bulk_row = present(bulk.state.rows[item.artkey], 'bulk row')
                                if (!bulk._fields[item.artkey]) {
                                    bulk._fields[item.artkey] = bulk.fields(bulk_row)
                                }
                                return (
                                    <CollectionBulkColumn
                                        collection={vnode.attrs.collection}
                                        column_id={column.id}
                                        row={bulk_row}
                                    />
                                )
                            }

                            if (this.has_row_status && is_last_column) {
                                rendered_row = row_status?.render
                            } else {
                                rendered_row = column.render(item, vnode.attrs.context)
                            }

                            let cell_type = 'default'
                            if (column.type && typeof column.type === 'function') {
                                cell_type = column.type(item)
                            }
                            return (
                                <div className={classes('cell', column.className, `cell-type-${cell_type}`)}>{rendered_row}</div>
                            )
                        })}

                        {vnode.attrs.row_actions && (
                            <div className={'cell cell-actions row-actions no-click on-chrome'}>
                                {(() => {
                                    const workflow_filter = meta_workflow_filter(vnode.attrs.collection)
                                    const active_workflow_id = workflow_filter?.selection
                                    const workflows = workflow_filter?.context || []
                                    const active_workflow = active_workflow_id
                                        ? workflows.find((wf) => wf.workflow_id === active_workflow_id)
                                        : null
                                    const workflow_action_labels = active_workflow?.actions.map((a) => a.label) || []

                                    return vnode.attrs.row_actions(item, vnode.attrs.context, {
                                        is_active: !!active_workflow_id,
                                        action_labels: workflow_action_labels,
                                    })
                                })()}
                            </div>
                        )}
                        {(() => {
                            const bulk_row_actions = vnode.attrs.collection.bulk?.bulk_row_actions?.(item)
                            if (!bulk_row_actions || bulk_row_actions.length === 0) {
                                return null
                            }

                            return (
                                <div className='cell cell-actions bulk-row-actions no-click'>
                                    {bulk_row_actions.map((action) => action)}
                                </div>
                            )
                        })()}
                    </div>
                    <>
                        {row_detail && vnode.attrs.collection.state.detail.artkey === item.artkey && (
                            <div className='row-details no-click' key={vnode.attrs.collection.state.detail.key}>
                                {row_detail}
                            </div>
                        )}
                    </>
                </div>,
            ]
            if (row_separator) {
                item_elements.unshift(
                    <div key={item.artkey} className={classes('row-separator', `type-${row_separator.type}`)}>
                        {row_separator.icon && <Icon name={row_separator.icon} type='unset' />}
                        {row_separator.text}
                    </div>,
                )
            }
            return item_elements
        })
    }

    /**
     * Publishes the window for CollectionStatus and loads what it lacks. Never reading the range back keeps
     * this view from redrawing itself for its own write; equal values don't notify at all.
     */
    private sync_window(collection: CollectionProxy<Row>, viewport: ReturnType<CollectionItems<Row>['calculate_viewport']>) {
        const start = viewport.start_index + 1
        const range = {end: Math.max(start, viewport.end_index), start}
        const published = collection.state.view.viewport_range
        published.start = range.start
        published.end = range.end
        if (viewport.item_height > 0) {
            void this.maybe_load_more(
                collection,
                viewport.load_start_index,
                viewport.load_end_index - viewport.load_start_index,
                range,
            )
        }
    }

    private render_virtualized(vnode: m.Vnode<CollectionItemsAttrs<Row>>) {
        const collection = vnode.attrs.collection
        const view = collection.state.view
        const total = vnode.attrs.collection.state.total ?? 0

        // Correct the column count before sizing the window, so a redraw that lands after a mode/width change
        // (e.g. the toggle) can't render a window computed from a stale, collapsed column count.
        this.reconcile_columns_if_layout_shifted(collection)

        const viewport = this.calculate_viewport(collection, total, true)
        const {start_index, end_index, start_row, end_row, total_rows} = viewport
        this.sync_window(collection, viewport)

        const {heights, row_tops, gap} = this.compute_virtual_geometry(collection, total)
        let window_px = 0
        for (let r = start_row; r <= end_row; r++) {
            const height = heights[r]
            if (height !== undefined) {
                window_px += height
                if (r < end_row) {
                    window_px += gap
                }
            }
        }
        const top_px = row_tops[start_row] ?? 0
        const total_scroll = row_tops[total_rows] ?? 0
        const bottom_px = Math.max(0, total_scroll - top_px - window_px)

        const elements: m.Children[] = []
        // Use cached grid element reference - ensure it's always cached after first lookup
        if (!this.items_container && !globalThis.__SSR_MODE__) {
            this.items_container = document.getElementById(this.id)
        }

        if (this.items_container) {
            this.items_container.style.paddingTop = `${top_px}px`
            // Fold the bottom gutter into the spacer so it survives at the end of the list, where bottom_px is 0.
            this.items_container.style.paddingBottom = `${bottom_px + this.bottom_gutter_px}px`
        }

        const on_item_height_change = (global_index: number, height_px: number) =>
            this.on_virtual_item_height(collection, global_index, height_px)

        for (let i = start_index; i < end_index; i++) {
            const is_real_item = i >= view.page_base_offset && i < view.page_base_offset + collection.state.items.length
            let item_at_i: Row | null = null

            if (is_real_item) {
                // Item is in state.items - use it directly
                const array_index = i - view.page_base_offset
                if (array_index >= 0 && array_index < collection.state.items.length) {
                    item_at_i = state_rows(collection)[array_index] ?? null
                }
            } else if (collection.state.view.cache) {
                const cached_entry = collection.is_item_cached(i)
                if (cached_entry) {
                    item_at_i = cached_entry.item
                }
            }

            // Key every windowed element by its absolute index. Without a key the children diff is
            // position-based, so as the window slides each fixed DOM slot is re-patched with a different
            // item — reloading its <img> every scroll step (visible blink). Keyed by index, the overlap
            // between frames keeps its DOM (and loaded images) untouched; only the entering/leaving rows
            // mount/unmount. A key set by the item renderer itself wins.
            const el = vnode.attrs.item?.(item_at_i, {global_index: i, on_item_height_change})
            if (el && typeof el === 'object' && !Array.isArray(el) && el.key == null) {
                el.key = i
            }
            elements.push(el)
        }

        return elements
    }

    private async schedule_expired_refresh(collection: CollectionProxy<Row>, viewport: {start: number; end: number}) {
        const visible_offset = Math.max(0, viewport.start - 1)
        const visible_limit = Math.max(0, viewport.end - viewport.start)
        if (visible_limit <= 0) {
            return
        }

        const cache_snapshot = collection.get_cached_items(visible_offset, visible_limit)
        if (cache_snapshot.has_missing || cache_snapshot.expired_indices.length === 0) {
            return
        }

        // Use all_items (includes expired) to show stale data while refreshing
        const items = cache_snapshot.all_items.filter((item): item is Row => item !== null)
        if (items.length === visible_limit) {
            this.synchronize_cached_window(collection, visible_offset, items)
        }

        const expired_set = new Set(cache_snapshot.expired_indices)
        const {items: refreshed_items, status_code} = await collection.fetch_items(visible_offset, visible_limit)
        if (status_code > 299 || refreshed_items.length === 0) {
            return
        }

        collection.set_cached_items(visible_offset, refreshed_items)

        const view = collection.state.view
        for (let i = 0; i < refreshed_items.length; i++) {
            const global_index = visible_offset + i
            if (!expired_set.has(global_index)) {
                continue
            }
            const state_idx = global_index - view.page_base_offset
            if (state_idx >= 0 && state_idx < collection.state.items.length) {
                merge_deep(collection.state.items[state_idx], refreshed_items[i])
            }
        }
    }

    private synchronize_cached_window(collection: CollectionProxy<Row>, target_offset: number, cached_items: Row[]) {
        const view = collection.state.view
        const is_loading_before = target_offset < view.page_base_offset
        const is_gap_forward = target_offset > view.page_base_offset + collection.state.items.length
        // Check if we're reloading expired items at the same offset (need to replace, not append)
        const is_reloading_expired =
            target_offset >= view.page_base_offset && target_offset < view.page_base_offset + collection.state.items.length

        if (is_loading_before || is_gap_forward) {
            collection.state.items.splice(0, collection.state.items.length, ...cached_items)
            view.page_base_offset = target_offset
        } else if (is_reloading_expired) {
            const array_start = target_offset - view.page_base_offset
            for (let i = 0; i < cached_items.length; i++) {
                const state_idx = array_start + i
                if (state_idx < collection.state.items.length) {
                    merge_deep(collection.state.items[state_idx], cached_items[i])
                }
            }
        }

        collection.state.has_items = collection.state.items.length < (collection.state.total ?? 0)
    }

    view(vnode: m.Vnode<CollectionItemsAttrs<Row>>) {
        const collection = vnode.attrs.collection
        const loading = collection.state.loading
        const items = state_rows(collection)
        // `pending`: the collection opted into `poll_when_not_ready` and the endpoint still answers 404 (its
        // data isn't generated yet). Show a waiting widget and keep polling instead of the empty "no results".
        const pending = collection.state.content_state === 'pending'
        if (pending) {
            this.schedule_not_ready_poll(collection)
        }

        return [
            <div
                className={classes('c-collection-items', vnode.attrs.className, {
                    'bulk-mode': vnode.attrs.collection.state.bulk_mode,
                    'has-row-click': 'on_row_click' in vnode.attrs,
                    'has-row-status': this.has_row_status && !vnode.attrs.row_status_no_cell_status,
                })}
                id={this.id}
            >
                {!items.length && pending && (
                    <div className='collection-feedback'>
                        <div className='feedback-icon'>
                            <Spinner />
                        </div>
                        <span>{$t('collection.pricelist_pending')}</span>

                        <div className='suggest mt-1'>{$t('collection.pricelist_pending_message')}</div>
                    </div>
                )}
                {!items.length &&
                    !loading &&
                    !pending &&
                    (() => {
                        const workflow_filter = meta_workflow_filter(vnode.attrs.collection)
                        const is_workflow_active = !!workflow_filter?.selection
                        let filters_activated = activated_filters(vnode.attrs.collection)

                        // Show workflow complete placeholder when workflow is active
                        if (is_workflow_active) {
                            return (
                                <div className='collection-feedback'>
                                    <div className='feedback-icon'>
                                        <Icon name='checked' type='success' />
                                    </div>
                                    <span>{$t('collection.workflow_complete')}</span>

                                    <div className='suggest mt-1'>{$t('collection.workflow_complete_message')}</div>
                                </div>
                            )
                        }

                        // Show no results placeholder when no workflow is active
                        return (
                            <div className='collection-feedback'>
                                <div className='feedback-icon'>
                                    <Icon name='notfound' />
                                </div>
                                <span>{$t('collection.no_results')}</span>

                                {filters_activated !== 0 && (
                                    <div className='suggest mt-1'>
                                        {/* Same icon + brand type as the CollectionFilterControl reset button
                                            (the red one in the header) so the reset action reads as one thing. */}
                                        <Icon
                                            name='content_save_off_outline'
                                            type='brand'
                                            onclick={() => {
                                                void reset_filters_sort(vnode.attrs.collection)
                                            }}
                                        />
                                        <span>{$t('collection.no_results_clear_filters')}</span>
                                    </div>
                                )}
                            </div>
                        )
                    })()}
                {vnode.attrs.virtualize ? this.render_virtualized(vnode) : this.render(vnode, items)}
            </div>,
            !vnode.attrs.virtualize && !vnode.attrs.disable_scroll && <div class='loading-observable'></div>,
        ]
    }

    watch_filters = debounce(100, async function (this: CollectionItems<Row>, vnode: m.Vnode<CollectionItemsAttrs<Row>>) {
        vnode.attrs.collection.state.loading = true
        // Last minute filter value check; a filter may get a value
        // which results in resetting a filter(e.g. range filters).
        // This can be done from here.
        for (const filter of Object.values(vnode.attrs.collection.filters)) {
            // A range filter that needs to be reset.
            if ('min' in filter && Array.isArray(filter.selection) && filter.selection.length >= 2) {
                const sel: number[] = filter.selection
                if (sel[0] === filter.min && (sel[1] === filter.max || sel[1] === Infinity)) {
                    clear_filter(filter)
                }
            }
        }

        if (this.debounced_cancel) {
            return
        }

        vnode.attrs.collection.state.query.offset = 0
        await lookup_filters_sort(vnode.attrs.collection)
        this.scroll_container?.scrollTo({behavior: 'instant', top: 0})

        if (!vnode.attrs.virtualize) {
            void vnode.attrs.collection.query()
            return
        }

        vnode.attrs.collection.state.items.splice(0, vnode.attrs.collection.state.items.length)
        if (vnode.attrs.collection.state.view.cache) {
            vnode.attrs.collection.clear_cache()
        }
        // Reset viewport tracking when filters change
        this.last_viewport_range = null
        this.clear_item_pixel_heights()
        const view = vnode.attrs.collection.state.view
        view.page_base_offset = 0
        // Clear items to ensure fresh state

        // Calculate viewport range first (using current total, will be updated after query)
        // This ensures we load the correct range with overscan, matching what render_virtualized expects
        const current_total = vnode.attrs.collection.state.total ?? 0
        const viewport = this.calculate_viewport(vnode.attrs.collection, current_total, true)
        await this.perform_network_load(vnode.attrs.collection, 0, viewport.load_end_index)
    })

    watch_select_all(vnode: m.Vnode<CollectionItemsAttrs<Row>>) {
        const selection = vnode.attrs.collection.state.selection
        // `current` holds the items the backend already reports as selected.
        if (selection.all) {
            selection.ids.splice(0, selection.ids.length)
        } else {
            selection.ids.splice(0, selection.ids.length, ...selection.current)
        }
    }

    watch_select_mode(vnode: m.Vnode<CollectionItemsAttrs<Row>>) {
        const selection = vnode.attrs.collection.state.selection
        if (vnode.attrs.on_selection_mode_change) {
            vnode.attrs.on_selection_mode_change(selection.mode)
        }
    }

    watch_sort = debounce(100, function (this: CollectionItems<Row>, vnode: m.Vnode<CollectionItemsAttrs<Row>>) {
        if (!this.debounced_cancel) {
            // Clear cache when sort changes
            const prev_items_count = vnode.attrs.collection.state.items.length
            const view = vnode.attrs.collection.state.view
            const prev_offset = view.page_base_offset
            // Clear all cache entries when sort changes
            if (view.cache) {
                vnode.attrs.collection.clear_cache()
            }
            // Reset viewport tracking when sort changes
            this.last_viewport_range = null
            // Reset the query offset; so all results are replaced after
            // changing the sort direction.
            vnode.attrs.collection.state.query.offset = 0
            // Reset page_base_offset to match the new query starting at offset 0
            view.page_base_offset = 0
            // Clear items immediately to prevent rendering stale items with wrong indices
            vnode.attrs.collection.state.items.splice(0, vnode.attrs.collection.state.items.length)
            logger.debug(
                `[items] sort changed: cleared ${prev_items_count} items, offset ${prev_offset} -> ${view.page_base_offset}`,
            )
            void lookup_filters_sort(vnode.attrs.collection)

            this.scroll_container?.scrollTo({behavior: 'auto', top: 1})
            void vnode.attrs.collection.query()
        }
    })
}
